<script setup lang="tsx">
import * as Chartist from 'chartist'
import { ref, onMounted, watch, nextTick, computed } from 'vue'
import http from '@/services/http'
import type { RiskResponsibleDeadlinesResponse, Response, Responsible } from '@/models/api-generated'
import { useI18n } from 'vue-i18n'
import { hasPermission } from '@/services/utils'
import { PermOwnRiskRead, PermAllRiskRead } from '@/models/enums-generated'
import Pagination from '@/components/widgets/PaginationComponent.vue'
import type { PaginationState } from '@/types/page'
import type { RiskOverviewFilter } from '@/models/filters-generated'

const props = defineProps<{
	filter: RiskOverviewFilter
}>()

const currentUser = sessionMap.user
const chart = ref(null)
const result = ref<RiskResponsibleDeadlinesResponse>()
const style = ref<string>('')
const currentPage = ref<PaginationState>({ page: 1, limit: 5 })
const { t } = useI18n()
const canViewRisks = computed(() => hasPermission(PermOwnRiskRead, PermAllRiskRead))

async function fetchData() {
	if (!canViewRisks.value) {
		result.value = { responsibles: [], totalPages: 0 }
		return
	}

	const { data } = await http.post<Response<RiskResponsibleDeadlinesResponse>>(
		`api/risk/responsibleDeadlines/${currentPage.value.page}`,
		props.filter,
	)
	result.value = data.data
}

const emit = defineEmits<{
	(e: 'go-to-risks', riskNames: string[], treatments: string[], responsible: Responsible): void
}>()

function goToRisks(responsible: Responsible) {
	emit('go-to-risks', [], [], responsible)
}

const opts: Chartist.BarChartOptions = {
	horizontalBars: true,
	reverseData: true,
	axisX: {
		onlyInteger: true,
		showGrid: false,
		showLabel: false,
	},
	axisY: {
		offset: 100,
	},
}

let barChart: Chartist.BarChart
const responsibleData = ref<Array<Responsible | undefined>>([])

onMounted(async () => {
	barChart = new Chartist.BarChart(chart.value, { series: [] }, opts)

	barChart.on('draw', (e) => {
		if (e.type === 'bar') {
			const value = Chartist.isMultiValue(e.value) ? (e.value as any).x : (e.value as number)
			const respData = responsibleData.value[e.index]

			const barElement = e.element.getNode()
			barElement.setAttribute('title', `${t('risk.overview.overDeadline')}: ${value}`)
			barElement.setAttribute('data-bs-toggle', 'tooltip')
			barElement.setAttribute('class', 'ct-bar overdue')

			if (value > 0 && respData) {
				barElement.addEventListener('click', () => goToRisks(respData))

				new Chartist.Svg(
					'text',
					{
						x: e.x2 + 4,
						y: e.y1,
						'dominant-baseline': 'central',
						fill: '#dc3545',
					},
					undefined,
					e.group,
				).text(value.toString())
			}
		}
	})

	await fetchData()
})

function updateChart() {
	if (!result.value || result.value.responsibles.length === 0) {
		style.value = 'height:100px;'
		return
	}

	const labels = result.value.responsibles.map((item) => item.responsible?.label || t('misc.nothing'))
	const overdue = result.value.responsibles.map((item) => item.overdueDates)
	responsibleData.value = result.value.responsibles.map(
		(item) => item.responsible ?? ({ id: 0, type: '', label: t('misc.nothing') } as Responsible),
	)
	const height = labels.length * 60
	style.value = `height:${height}px;`

	responsibleData.value.reverse()

	nextTick(() => {
		barChart.update({
			labels,
			series: [overdue],
		})
	})
}

watch(result, updateChart)
watch(() => props.filter, fetchData, { deep: true })
watch(currentPage, fetchData, { deep: true })
</script>

<template>
	<div class="rounded-container p-4 overflow-auto text-nowrap">
		<p class="text-center fw-bold">
			{{ $t('risk.overview.responsibleDeadlines') }}
			<template v-if="hasPermission(PermOwnRiskRead) && !hasPermission(PermAllRiskRead)">
				- {{ currentUser.firstname }} {{ currentUser.lastname }}
			</template>
			<template v-else-if="!canViewRisks"> - {{ $t('roles.missingRights') }} </template>
		</p>

		<div ref="chart" class="risk-responsible-chart" :style="style"></div>

		<Pagination v-if="result" v-model="currentPage" :total-pages="result.totalPages ?? 0" :disableLimitSelect="true" />

		<div v-if="!result" class="p-4 text-center">
			<div class="spinner-border text-primary"></div>
		</div>
	</div>
</template>

<style lang="sass">
.risk-responsible-chart
    .ct-label
        color: var(--bs-body-color) !important

    .ct-bar
        cursor: pointer
        stroke-width: 15px
        transition: opacity 0.2s ease

        &:hover
            opacity: 0.7

        &.overdue
            stroke: #dc3545
</style>
